import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Grid, Container, Breadcrumbs, Link, IconButton, CircularProgress, useTheme, Divider } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import MainDrawer, { openDrawerWidth, collapsedDrawerWidth } from "../../MainDrawer";
import queryGraphql from "../../../helpers/ApiInterface";
import MiniBot from '../../MiniBot';
import { useAuth } from '../../../helpers/AuthProvider';

const DetailItem: React.FC<{ label: string; value?: string | number | boolean | null }> = ({ label, value }) => {
    const displayValue = value !== null && value !== undefined && value !== '' ? value : '-';
    return (
        <Box mb={2}>
            <Typography fontFamily="Gilroy-Regular" fontSize="12px" color="text.secondary">{label}</Typography>
            <Typography fontFamily="Gilroy-Semibold" fontSize="14px" sx={{ wordBreak: 'break-all' }}>{typeof displayValue === 'boolean' ? (displayValue ? 'Enabled' : 'Disabled') : displayValue}</Typography>
        </Box>
    );
};

interface SolutionOwnerData {
    id: string;
    name: string;
}

interface ParentSolutionData {
    id: string;
    name: string;
}

interface DataplexZoneDetails {
    projectId: string;
    location: string;
    type: string;
    description?: string;
    discoverySpec?: {
        enabled: boolean;
    }
}

interface DataZoneData {
    id: string;
    name: string;
    reasonForRejection: string;
    cloudProvider: 'gcp' | 'aws' | 'azure' | string;
    details: {
        dataplexZone: DataplexZoneDetails;
    };
    status: string;
    solutionOwner: SolutionOwnerData;
    parentSolution: ParentSolutionData;
}

const DataZonesDetailsPageContent: React.FC = () => {
    const navigate = useNavigate();
    const {id}  = useParams();
    const [dataZone, setDataZone] = useState<DataZoneData | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const { user } = useAuth();
    const [dynamicLabels, setDynamicLabels] = useState({
        projectId: 'Project ID',
    });

    useEffect(() => {
        const fetchDataZoneData = async () => {
            if (!id) {
                setIsLoading(false);
                setError("Data Zone ID is missing from URL.");
                return;
            }

            setIsLoading(true);
            setError(null);
            try {
                const query = `
                    query GetDataplexZone($getDataplexZoneId: String!) {
                      getDataplexZone(id: $getDataplexZoneId) {
                        id
                        name
                        reasonForRejection
                        cloudProvider
                        status
                        details {
                          dataplexZone {
                            projectId
                            location
                            type
                            description
                            discoverySpec {
                                enabled
                            }
                          }
                        }
                        solutionOwner {
                          id
                          name
                        }
                        parentSolution {
                            id
                            name
                        }
                      }
                    }
                `;
                const response = await queryGraphql(query, { getDataplexZoneId: id });
                
                if (response.getDataplexZone) {
                    setDataZone(response.getDataplexZone);
                } else if (response.errors && response.errors.length > 0) {
                    setError(response.errors[0].message || "Error fetching data.");
                    setDataZone(null);
                } else {
                    setError("Data Zone not found or unexpected response structure.");
                    setDataZone(null);
                }
            } catch (err: any) {
                console.error("Error fetching Data Zone data:", err);
                setError(err.message || "Failed to fetch Data Zone details.");
                setDataZone(null);
            } finally {
                setIsLoading(false);
            }
        };

        fetchDataZoneData();
    }, [id]);
    
    useEffect(() => {
        if (dataZone?.cloudProvider) {
            switch (dataZone.cloudProvider) {
                case 'aws':
                    setDynamicLabels({ projectId: 'Account ID' }); break;
                case 'azure':
                    setDynamicLabels({ projectId: 'Subscription ID' }); break;
                case 'gcp':
                default:
                    setDynamicLabels({ projectId: 'Project ID' }); break;
            }
        }
    }, [dataZone]);


    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress size={24} />
                <Typography sx={{ ml: 2 }}>Loading data zone details...</Typography>
            </Box>
        );
    }

    if (error) {
        return <Typography color="error" sx={{p:2}}>{error}</Typography>;
    }

    if (!dataZone) {
        return <Typography sx={{p:2}}>No data zone data available.</Typography>;
    }

    const resourceOwnerName = dataZone.solutionOwner.name;
    const dzDetails = dataZone.details.dataplexZone;

    const getStatusBackgroundColor = (status: string | undefined): string => {
        if (!status) return "#F8F8F8";
        const lowerStatus = status.toLowerCase();
        if (lowerStatus === "active" || lowerStatus === "approved") return "#12B76A10";
        if (lowerStatus === "rejected") return "#F0443810";
        if (lowerStatus === "pending") return "#FDB02210";
        return "#F8F8F8";
    };

    return (
        <Container maxWidth={false} sx={{ mt: 2, mb: 2, pl: { xs: 2, sm: 3 }, pr: {xs: 2, sm: 3} }}>
            <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2, fontSize: "14px", color: "gray" }}>
                <Link underline="hover" color="inherit" href="/datasolutions/data-zones" sx={{fontFamily:'Gilroy-regular'}} onClick={(e) => { e.preventDefault(); navigate('/datasolutions/data-zones'); }}> Data Zones </Link>
                <Typography color="text.primary" sx={{fontSize: "14px", fontFamily:'Gilroy-Medium'}}>Data Zone Details</Typography>
            </Breadcrumbs>

            <Box display="flex" alignItems="center" mb={3}>
                <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate('/datasolutions/data-zones')}>
                    <ArrowBackIosNewIcon fontSize="small" />
                </IconButton>
                <Typography variant="h5" component="h1" sx={{ fontFamily:'Gilroy-Bold',fontSize: "18px"}}> Data Zone Details </Typography>
            </Box>

            <Box sx={{mx:0, boxShadow: "0px 3px 6px #10101029", borderRadius: "8px" }}>
                <Box sx={{ p: 2.5, bgcolor: getStatusBackgroundColor(dataZone.status), mb: 0, mx: 0, borderTopLeftRadius: "8px", borderTopRightRadius: "8px" }}>
                    <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                            <Typography variant="h6" component="h2" sx={{ fontFamily: 'Gilroy-Bold', fontSize: '20px' }}>
                                {dataZone.name}
                            </Typography>
                            {user.roles?.some((e: string) => ["cluster_owner"].includes(e)) && dataZone.status === "pending" && (
                                <IconButton sx={{ p: 0.5 }} edge="end" aria-label="edit" color="inherit" onClick={() => navigate(`/datasolutions/data-zones/edit/${id}`)}>
                                    <img width="20px" height="20px" src="/assets/edit-icon.svg" alt="Edit"/>
                                </IconButton>
                            )}
                        </Box>
                        <Typography variant="body2" sx={{ fontFamily:'Gilroy-Regular', fontSize:'14px', color:'#666666' }}>
                            Resource Owner: {resourceOwnerName}
                        </Typography>
                    </Box>
                </Box>

                <Grid container spacing={3} sx={{p:3, pt: 2.5 }}>
                    <Grid item xs={12} sm={4} md={3}><DetailItem label="Parent Lake" value={dataZone.parentSolution?.name} /></Grid>
                    <Grid item xs={12} sm={4} md={3}><DetailItem label="Zone Type" value={dzDetails.type} /></Grid>
                    <Grid item xs={12} sm={4} md={3}><DetailItem label={dynamicLabels.projectId} value={dzDetails.projectId} /></Grid>
                    <Grid item xs={12} sm={4} md={3}><DetailItem label="Location" value={dzDetails.location} /></Grid>
                    <Grid item xs={12} sm={4} md={3}><DetailItem label="Cloud Provider" value={dataZone.cloudProvider?.toUpperCase()} /></Grid>
                </Grid>
            </Box>

              {dataZone.status === 'rejected' && (
                    <Box mt={2} bgcolor="#fff" p={3} borderRadius="12px" boxShadow="0px 3px 6px #10101029">
                        <Typography fontSize="14px" fontFamily="Gilroy-SemiBold">Rejection Details</Typography>
                        <Divider sx={{ marginY: 2 }} />
                        <DetailItem label="Reason for Rejection" value={dataZone.reasonForRejection} />
                    </Box>
                )}

                {dataZone.status === 'active' && (
                    <Box mt={2} bgcolor="#fff" p={3} borderRadius="12px" boxShadow="0px 3px 6px #10101029">
                        <Typography fontSize="14px" fontFamily="Gilroy-SemiBold">Data Zone Specifications</Typography>
                        <Divider sx={{ marginY: 2 }} />
                        <Grid container spacing={2.5}>
                            <Grid item xs={12}><DetailItem label="Description" value={dzDetails.description} /></Grid>
                            <Grid item xs={12} sm={6} md={3}><DetailItem label="Discovery" value={dzDetails.discoverySpec?.enabled} /></Grid>
                        </Grid>
                    </Box>
                )}
        </Container>
    );
};


const DataZonesDetailsPage: React.FC = () => {
    const theme = useTheme();
    const getInitialDrawerState = () => {
        if (typeof window !== 'undefined') {
            const storedState = localStorage.getItem('isFaradayDrawerOpen');
            return storedState !== null ? JSON.parse(storedState) : true;
        }
        return true;
    };
    const [isDrawerActuallyOpen, setIsDrawerActuallyOpen] = useState<boolean>(getInitialDrawerState);

    const handleDrawerStateChange = (isOpenFromDrawer: boolean) => {
        setIsDrawerActuallyOpen(isOpenFromDrawer);
        if (typeof window !== 'undefined') {
            localStorage.setItem('isFaradayDrawerOpen', JSON.stringify(isOpenFromDrawer));
        }
    };

    const currentMarginLeft = isDrawerActuallyOpen ? openDrawerWidth - 248 : collapsedDrawerWidth - 70;

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <MainDrawer
                index={"datasolutions-data-zones"}
                onStateChange={handleDrawerStateChange}
            />
            <MiniBot/>
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 0,
                    transition: theme.transitions.create('margin', {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.enteringScreen,
                    }),
                    marginLeft: {xs: 0, sm: `${currentMarginLeft}px`},
                }}
            >
                <DataZonesDetailsPageContent />
            </Box>
        </Box>
    );
};

export default DataZonesDetailsPage;